import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Camera, Loader2, RotateCcw, X, ImageOff } from "lucide-react";

export const STATUS_META = {
  ok: { label: "OK", cls: "border-emerald-500/60 text-emerald-300 bg-emerald-500/10" },
  anomalia: { label: "Anomalia", cls: "border-rose-500/60 text-rose-300 bg-rose-500/10" },
  non_verificato: { label: "Non verificato", cls: "border-slate-500/60 text-slate-300 bg-slate-500/10" },
};
export const REPORT_STATUS = {
  aperta: { label: "Aperta", cls: "border-rose-500/50 text-rose-300" },
  in_verifica: { label: "In verifica", cls: "border-sky-500/50 text-sky-300" },
  attesa_intervento: { label: "In attesa di intervento", cls: "border-amber-500/50 text-amber-300" },
  risolta: { label: "Risolta", cls: "border-emerald-500/50 text-emerald-300" },
};
export const CATEGORY_LABEL = {
  danno_estetico: "Danno estetico", meccanico: "Problema meccanico", sicurezza: "Spia o problema di sicurezza",
  pulizia: "Pulizia", manutenzione: "Manutenzione", altro: "Altro",
};
export const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export function PhotoThumb({ id, size = "w-20 h-20", onOpen }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let u;
    api.get(`/vehicle-checks/photos/${id}`, { responseType: "blob" })
      .then(r => { u = URL.createObjectURL(r.data); setUrl(u); })
      .catch(() => setErr(true));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [id]);
  if (err) return <div className={`${size} rounded-lg border border-slate-800 flex items-center justify-center text-slate-600`}><ImageOff size={16} /></div>;
  if (!url) return <div className={`${size} rounded-lg border border-slate-800 bg-slate-900 animate-pulse`} />;
  return <button type="button" onClick={() => onOpen?.(url)} className={`${size} rounded-lg overflow-hidden border border-amber-500/30`} data-testid={`vc-photo-${id}`}><img src={url} alt="foto" className="w-full h-full object-cover" /></button>;
}

export function PhotoLightbox({ url, onClose }) {
  if (!url) return null;
  return (
    <div className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center p-4" onClick={onClose} data-testid="vc-lightbox">
      <img src={url} alt="foto" className="max-w-full max-h-full rounded-xl" />
      <button type="button" className="absolute top-4 right-4 text-white" onClick={onClose}><X /></button>
    </div>
  );
}

/** Upload con stato per singola foto: un errore non cancella il resto del modulo. */
export function PhotoPicker({ vehicleId, value, onChange, testid = "vc-photo-picker" }) {
  const input = useRef(null);
  const [pending, setPending] = useState([]);
  const upload = async (file, key) => {
    setPending(p => p.map(x => x.key === key ? { ...x, state: "uploading" } : x));
    const fd = new FormData();
    fd.append("file", file);
    fd.append("vehicle_id", vehicleId);
    try {
      const r = await api.post("/vehicle-checks/photos", fd, { timeout: 120000 });
      setPending(p => p.filter(x => x.key !== key));
      onChange(ids => [...ids, r.data.id]);
    } catch (e) {
      setPending(p => p.map(x => x.key === key ? { ...x, state: "error", error: formatApiError(e) } : x));
    }
  };
  const pick = (files) => {
    [...files].forEach(f => {
      const key = Math.random().toString(36).slice(2);
      setPending(p => [...p, { key, file: f, state: "uploading" }]);
      upload(f, key);
    });
  };
  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid={testid}>
      {value.map(id => (
        <div key={id} className="relative">
          <PhotoThumb id={id} size="w-16 h-16" />
          <button type="button" onClick={() => onChange(ids => ids.filter(x => x !== id))} className="absolute -top-1.5 -right-1.5 bg-black border border-slate-700 rounded-full p-0.5 text-slate-300"><X size={11} /></button>
        </div>
      ))}
      {pending.map(p => (
        <div key={p.key} className={`w-16 h-16 rounded-lg border flex flex-col items-center justify-center text-[10px] text-center ${p.state === "error" ? "border-rose-500/60 text-rose-300" : "border-slate-700 text-slate-400"}`}>
          {p.state === "uploading" ? <Loader2 size={16} className="animate-spin" /> : (
            <button type="button" onClick={() => upload(p.file, p.key)} className="flex flex-col items-center" title={p.error} data-testid="vc-photo-retry"><RotateCcw size={14} />Riprova</button>
          )}
        </div>
      ))}
      <button type="button" onClick={() => input.current?.click()} className="w-16 h-16 rounded-lg border border-dashed border-amber-500/50 text-amber-300 flex flex-col items-center justify-center text-[10px]" data-testid={`${testid}-add`}>
        <Camera size={18} />Foto
      </button>
      <input ref={input} type="file" accept="image/*" multiple className="hidden" onChange={e => { pick(e.target.files); e.target.value = ""; }} />
    </div>
  );
}
